import { useEffect, useRef, useState, type FormEvent } from "react";
import { AdminUsersApiError, type AdminUsersClient } from "./admin-users-api";
import type {
  AccessChange,
  ManagedUserAccess,
  UserAccessDirectory,
} from "./user-access-api";
import { useLocale } from "./localization";
import { userAccessCopy } from "./user-access-copy";
import "./user-access.css";

export const USERS_CHANGED_EVENT = "tu-users-changed";

function AccessEditor({
  user,
  conferences,
  save,
  onDirty,
}: {
  user: ManagedUserAccess;
  conferences: UserAccessDirectory["conferences"];
  save: (change: AccessChange) => Promise<void>;
  onDirty: (dirty: boolean) => void;
}) {
  const { locale, number } = useLocale();
  const text = userAccessCopy[locale === "en" ? "en" : "ar"];
  const [role, setRole] = useState(user.role);
  const [mode, setMode] = useState(user.conferenceAccess.mode);
  const [ids, setIds] = useState<readonly string[]>(
    user.conferenceAccess.mode === "selected"
      ? user.conferenceAccess.conferenceIds
      : [],
  );
  const [search, setSearch] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const promote = role === "admin" && user.role !== "admin";
  const selectedIds = [...ids].sort().join("|");
  const previousIds =
    user.conferenceAccess.mode === "selected"
      ? [...user.conferenceAccess.conferenceIds].sort().join("|")
      : "";
  const dirty =
    role !== user.role ||
    (role === "viewer" &&
      (mode !== user.conferenceAccess.mode ||
        (mode === "selected" && selectedIds !== previousIds)));
  useEffect(() => {
    onDirty(dirty);
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, onDirty]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user.canEdit || saving || (promote && !confirmed)) return;
    setSaving(true);
    setFailed(false);
    try {
      await save({
        role,
        conferenceAccess:
          role === "admin" || mode === "all"
            ? { mode: "all" }
            : { mode: "selected", conferenceIds: ids },
        expectedRevision: user.revision,
      });
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };
  const matches = conferences.filter((item) =>
    `${item.name} ${item.displayNames?.ar ?? ""} ${item.displayNames?.en ?? ""}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  return (
    <form
      className="user-access-editor"
      onSubmit={(event) => void submit(event)}
      aria-busy={saving}
    >
      {!user.canEdit && (
        <p className="access-note">
          {user.deletionPending ? text.deletionPendingHelp : text.protected}
        </p>
      )}
      <fieldset disabled={!user.canEdit || saving}>
        <legend className="visually-hidden">{text.role}</legend>
        <div className="access-field">
          <label htmlFor="user-access-role">{text.role}</label>
          <select
            id="user-access-role"
            name="role"
            value={role}
            onChange={(event) => {
              setRole(event.target.value as "admin" | "viewer");
              setConfirmed(false);
            }}
          >
            <option value="viewer">{text.viewer}</option>
            <option
              value="admin"
              disabled={user.status !== "active" || !user.mfaEnrolled}
            >
              {text.admin}
            </option>
          </select>
          {(user.status !== "active" || !user.mfaEnrolled) && (
            <p className="access-help">{text.pendingPromotion}</p>
          )}
        </div>
        {role === "admin" ? (
          <p className="access-note">{text.adminHelp}</p>
        ) : (
          <fieldset className="access-scope">
            <legend>{text.scope}</legend>
            <div className="access-scope-options">
              {(["all", "selected"] as const).map((option) => (
                <label
                  className={`access-scope-option${mode === option ? " is-selected" : ""}`}
                  key={option}
                >
                  <input
                    type="radio"
                    name="conference-scope"
                    value={option}
                    checked={mode === option}
                    onChange={() => setMode(option)}
                  />
                  <span>
                    <strong>{text[option]}</strong>
                    <small>
                      {option === "all" ? text.allShort : text.selectedShort}
                    </small>
                  </span>
                </label>
              ))}
            </div>
            {mode === "all" ? (
              <p className="access-help">{text.allHelp}</p>
            ) : (
              <div className="access-selection">
                <div className="access-selection-heading">
                  <label htmlFor="user-access-search">{text.search}</label>
                  <span>
                    {number(ids.length)} {text.selectedCount}
                  </span>
                </div>
                <input
                  id="user-access-search"
                  name="conference-search"
                  type="search"
                  autoComplete="off"
                  placeholder={text.searchPlaceholder}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <div
                  className="user-access-conferences"
                  role="group"
                  aria-label={text.scope}
                >
                  {!conferences.length ? (
                    <p className="access-help">{text.noConferences}</p>
                  ) : !matches.length ? (
                    <p className="access-help">{text.noMatches}</p>
                  ) : (
                    matches.map((item) => (
                      <label
                        className="user-access-checkbox"
                        key={item.conferenceId}
                      >
                        <input
                          type="checkbox"
                          name="conference"
                          value={item.conferenceId}
                          checked={ids.includes(item.conferenceId)}
                          disabled={
                            !ids.includes(item.conferenceId) &&
                            ids.length >= 100
                          }
                          onChange={(event) =>
                            setIds((current) =>
                              event.target.checked
                                ? [...current, item.conferenceId]
                                : current.filter(
                                    (id) => id !== item.conferenceId,
                                  ),
                            )
                          }
                        />
                        <span>
                          {item.displayNames?.[locale === "en" ? "en" : "ar"] ??
                            item.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <p className="access-help">{text.selectedHelp}</p>
              </div>
            )}
          </fieldset>
        )}
        {promote && (
          <label className="user-access-checkbox access-note">
            <input
              type="checkbox"
              name="confirm-admin"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span>{text.confirm}</span>
          </label>
        )}
        {user.canEdit && (
          <div className="access-editor-actions">
            <button
              className="button button--primary"
              type="submit"
              disabled={saving || (promote && !confirmed)}
            >
              {saving ? text.saving : text.save}
            </button>
            {dirty && <span className="access-help">{text.unsaved}</span>}
          </div>
        )}
      </fieldset>
      {failed && (
        <p className="access-notice access-notice--error" role="alert">
          {text.failed}
        </p>
      )}
    </form>
  );
}

function DeleteUserDialog({
  user,
  api,
  onClose,
  onDeleted,
}: {
  user: ManagedUserAccess;
  api: AdminUsersClient;
  onClose: () => void;
  onDeleted: (uid: string) => void;
}) {
  const { locale } = useLocale();
  const text = userAccessCopy[locale === "en" ? "en" : "ar"];
  const dialog = useRef<HTMLDialogElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const inFlight = useRef(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const close = () => {
    dialog.current?.close();
    onClose();
  };
  useEffect(() => {
    const element = dialog.current;
    element?.showModal();
    cancel.current?.focus();
    return () => element?.close();
  }, []);
  async function remove(event: FormEvent) {
    event.preventDefault();
    if (!confirmed || inFlight.current || !api.deleteUserAccess) return;
    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    try {
      await api.deleteUserAccess(user.authUid, user.revision);
      dialog.current?.close();
      onDeleted(user.authUid);
    } catch (error) {
      setFailure(
        error instanceof AdminUsersApiError &&
          (error.code === "access_conflict" || error.code === "user_not_found")
          ? text.deleteConflict
          : error instanceof AdminUsersApiError &&
              error.code === "protected_account"
            ? text.protected
            : text.deleteFailed,
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      className="access-delete-dialog"
      aria-labelledby="delete-user-title"
      aria-describedby="delete-user-description"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) close();
      }}
    >
      <form onSubmit={(event) => void remove(event)} aria-busy={busy}>
        <span className="access-danger-eyebrow">{text.permanent}</span>
        <h2 id="delete-user-title">{text.deleteTitle}</h2>
        <p className="access-delete-identity">
          <bdi dir="ltr" translate="no">
            {user.email || text.deletionPending}
          </bdi>
        </p>
        <p id="delete-user-description">{text.deleteDescription}</p>
        <p className="access-note">{text.deleteAudit}</p>
        <label className="user-access-checkbox">
          <input
            type="checkbox"
            name="confirm-delete-user"
            checked={confirmed}
            disabled={busy}
            onChange={(event) => setConfirmed(event.target.checked)}
          />
          <span>{text.deleteConfirm}</span>
        </label>
        {failure && (
          <p className="access-notice access-notice--error" role="alert">
            {failure}
          </p>
        )}
        <div className="access-dialog-actions">
          <button
            ref={cancel}
            className="button button--secondary"
            type="button"
            disabled={busy}
            onClick={close}
          >
            {text.cancel}
          </button>
          <button
            className="button access-delete-button"
            type="submit"
            disabled={!confirmed || busy}
          >
            {busy ? text.deleting : text.deletePermanently}
          </button>
        </div>
      </form>
    </dialog>
  );
}

export function UserAccessPanel({ api }: { api: AdminUsersClient }) {
  const { locale, number } = useLocale();
  const text = userAccessCopy[locale === "en" ? "en" : "ar"];
  const [directory, setDirectory] = useState<UserAccessDirectory | null>(null);
  const [selectedUid, setSelectedUid] = useState("");
  const [search, setSearch] = useState("");
  const [revision, setRevision] = useState(0);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<"saved" | "deleted" | null>(null);
  const [dirty, setDirty] = useState(false);
  const [deleting, setDeleting] = useState<ManagedUserAccess | null>(null);
  const deleteTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Other account actions must not discard a draft in this editor.
    const reload = () => {
      if (!dirty) setRevision((value) => value + 1);
    };
    window.addEventListener(USERS_CHANGED_EVENT, reload);
    return () => window.removeEventListener(USERS_CHANGED_EVENT, reload);
  }, [dirty]);
  useEffect(() => {
    if (!api.listUserAccess) return;
    let disposed = false;
    setFailed(false);
    setLoading(true);
    void api
      .listUserAccess()
      .then((value) => {
        if (!disposed) setDirectory(value);
      })
      .catch(() => {
        if (!disposed) setFailed(true);
      })
      .finally(() => {
        if (!disposed) setLoading(false);
      });
    return () => {
      disposed = true;
    };
  }, [api, revision]);
  if (!api.listUserAccess || !api.updateUserAccess) return null;
  const selected =
    directory?.users.find((user) => user.authUid === selectedUid) ??
    directory?.users[0];
  const visibleUsers =
    directory?.users.filter((user) =>
      user.email.toLowerCase().includes(search.trim().toLowerCase()),
    ) ?? [];
  function selectUser(uid: string) {
    if (uid === selected?.authUid) return;
    if (dirty && !window.confirm(text.discard)) return;
    setDirty(false);
    setSelectedUid(uid);
    setNotice(null);
  }
  return (
    <section
      className="user-access-panel user-access-panel--workspace"
      aria-labelledby="user-access-title"
    >
      <header className="access-workspace-header">
        <div>
          <h2 id="user-access-title">{text.title}</h2>
          <p>{text.description}</p>
        </div>
        <button
          className="button button--secondary button--compact"
          type="button"
          disabled={loading}
          onClick={() => {
            if (dirty && !window.confirm(text.discard)) return;
            setNotice(null);
            setDirty(false);
            setRevision((value) => value + 1);
          }}
        >
          {loading ? text.refreshing : text.refresh}
        </button>
      </header>
      {failed && (
        <p className="access-notice access-notice--error" role="alert">
          {text.loadFailed}
        </p>
      )}
      {notice && (
        <p className="access-notice access-notice--success" role="status">
          {text[notice]}
        </p>
      )}
      {!directory ? (
        <p role="status">{text.loading}</p>
      ) : !selected ? (
        <p>{text.empty}</p>
      ) : (
        <div className="access-workspace">
          <aside className="access-directory" aria-label={text.choose}>
            <div className="access-directory-title">
              <h3>{text.choose}</h3>
              <span>{number(directory.users.length)}</span>
            </div>
            <label
              className="visually-hidden"
              htmlFor="user-access-user-search"
            >
              {text.userSearch}
            </label>
            <input
              id="user-access-user-search"
              name="user-search"
              type="search"
              placeholder={text.userSearch}
              spellCheck={false}
              autoComplete="off"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="access-user-list">
              {!visibleUsers.length && (
                <p className="access-help">{text.noUsersMatch}</p>
              )}
              {visibleUsers.map((user) => (
                <button
                  type="button"
                  className={`access-user${selected.authUid === user.authUid ? " is-selected" : ""}`}
                  key={user.authUid}
                  data-user-id={user.authUid}
                  aria-pressed={selected.authUid === user.authUid}
                  onClick={() => selectUser(user.authUid)}
                >
                  <span className="access-avatar" aria-hidden="true">
                    {(user.email || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <span className="access-user-copy">
                    <strong>
                      <bdi dir="ltr" translate="no">
                        {user.email || text.deletionPending}
                      </bdi>
                    </strong>
                    <span>
                      {user.role === "admin" ? text.admin : text.viewerShort}
                      <span aria-hidden="true"> · </span>
                      {user.deletionPending
                        ? text.deletionPending
                        : text[user.status]}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </aside>
          <div className="access-user-detail">
            <header className="access-detail-heading">
              <div>
                <span className="access-eyebrow">{text.permissions}</span>
                <h3>
                  <bdi dir="ltr" translate="no">
                    {selected.email || text.deletionPending}
                  </bdi>
                </h3>
                <div className="access-detail-meta">
                  <span
                    className={`access-state access-state--${selected.status}`}
                  >
                    {selected.deletionPending
                      ? text.deletionPending
                      : text[selected.status]}
                  </span>
                  <span>
                    {selected.mfaEnrolled ? text.mfaReady : text.mfaPending}
                  </span>
                </div>
              </div>
            </header>
            <AccessEditor
              key={`${selected.authUid}:${selected.revision}:${revision}`}
              user={selected}
              conferences={directory.conferences}
              onDirty={setDirty}
              save={async (change) => {
                await api.updateUserAccess!(selected.authUid, change);
                setDirty(false);
                setDirectory(await api.listUserAccess!());
                setNotice("saved");
                window.dispatchEvent(new Event(USERS_CHANGED_EVENT));
              }}
            />
            {api.deleteUserAccess &&
              (selected.canDelete ?? selected.canEdit) && (
                <div className="access-danger-zone">
                  <div>
                    <strong>{text.deleteUser}</strong>
                    <p>{text.deleteHelp}</p>
                  </div>
                  <button
                    ref={deleteTrigger}
                    type="button"
                    className="button access-delete-link"
                    onClick={() => setDeleting(selected)}
                  >
                    {selected.deletionPending
                      ? text.retryDelete
                      : text.deleteUser}
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
      {deleting && (
        <DeleteUserDialog
          user={deleting}
          api={api}
          onClose={() => {
            setDeleting(null);
            deleteTrigger.current?.focus();
          }}
          onDeleted={(uid) => {
            setDeleting(null);
            setDirty(false);
            setDirectory((current) =>
              current
                ? {
                    ...current,
                    users: current.users.filter((user) => user.authUid !== uid),
                  }
                : current,
            );
            setSelectedUid("");
            setNotice("deleted");
            window.dispatchEvent(new Event(USERS_CHANGED_EVENT));
            document.getElementById("user-access-user-search")?.focus();
          }}
        />
      )}
    </section>
  );
}
